import React from 'react';
import { brandColors } from '../data/brandColors';
import { Link } from 'react-router-dom';

type CheckResult = {
  area: string;
  item: string;
  expected: string;
  found: string;
  confidence: number; // 0..100
  link?: string;
};

// Simple heuristic checker comparing canonical brand color names/hex
export const SourceOfTruthChecker: React.FC = () => {
  const checks: CheckResult[] = [];

  // Example checks between Visual Design Grammar and brand colors
  const grammarColors = [
    { key: 'primary', name: 'Sage Green', hex: '#5A7D5E', link: '/elevation/visual-design-grammar' },
    { key: 'secondary', name: 'Alpine Green', hex: '#53675B', link: '/elevation/visual-design-grammar' },
    { key: 'accent', name: 'Electric Mustard', hex: '#E3B505', link: '/elevation/visual-design-grammar' },
    { key: 'coral', name: 'Luminous Coral', hex: '#F85E5E', link: '/elevation/visual-design-grammar' },
  ] as const;

  grammarColors.forEach(g => {
    const canonical = brandColors[g.key as keyof typeof brandColors];
    const nameMatch = canonical.name === g.name;
    const hexMatch = canonical.hex.toLowerCase() === g.hex.toLowerCase();
    const confidence = Math.round(((nameMatch ? 0.5 : 0) + (hexMatch ? 0.5 : 0)) * 100);
    if (!nameMatch || !hexMatch) {
      checks.push({
        area: 'Visual Design Grammar',
        item: g.key,
        expected: `${canonical.name} ${canonical.hex}`,
        found: `${g.name} ${g.hex}`,
        confidence,
        link: g.link,
      });
    }
  });

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-charcoal-900">Source of Truth Checker</h1>
        <p className="text-charcoal-600">Compares canonical brand colors against usage in other pages and surfaces potential mismatches.</p>
      </div>

      <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-3">Canonical Brand Colors</h2>
        <ul className="grid md:grid-cols-2 gap-3">
          {Object.entries(brandColors).map(([key, c]) => (
            <li key={key} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded border border-charcoal-300" style={{ backgroundColor: c.hex }} />
              <span className="text-sm text-charcoal-700">{c.name}</span>
              <code className="text-xs bg-charcoal-50 px-2 py-0.5 rounded border border-charcoal-200">{c.hex}</code>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-3">Potential Issues</h2>
        {checks.length === 0 ? (
          <div className="text-sm text-sage-800 bg-sage-50 border border-sage-200 rounded p-4">No issues detected. All checked items match the canonical source.</div>
        ) : (
          <div className="space-y-3">
            {checks.map((c, i) => (
              <div key={i} className="flex items-start justify-between p-4 rounded border border-[var(--mustard)] bg-[var(--mustard-dim)]">
                <div>
                  <div className="text-sm font-semibold text-[var(--mustard)]">{c.area} — {c.item}</div>
                  <div className="text-sm text-charcoal-700">Expected: <code className="bg-[var(--bg-raised)] px-1 py-0.5 rounded border border-charcoal-200">{c.expected}</code></div>
                  <div className="text-sm text-charcoal-700">Found: <code className="bg-[var(--bg-raised)] px-1 py-0.5 rounded border border-charcoal-200">{c.found}</code></div>
                  {c.link && (
                    <Link to={c.link} className="text-xs text-consulting-slate-blue-700 hover:underline">View source</Link>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-charcoal-600">Confidence not aligned</div>
                  <div className="text-lg font-bold text-[var(--mustard)]">{c.confidence}%</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


